import * as React from "react";
import { Link } from "react-router";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * ActionButton — secondary-action-link für PageHeader action-cluster.
 *
 * Compact-format (h-10 = 40px, etwas unter touch-min 44px — akzeptabel im
 * action-cluster wegen gap-2-puffer, NICHT empfohlen für standalone-use).
 * White-bg + border, icon links + label rechts, hover-state.
 *
 * iconOnly: für scroll-overflow auf mobile (label wird zu aria-label).
 * disabled: für routes die noch nicht existieren (rendert als <span
 * aria-disabled> mit cursor-not-allowed — kein crash bei tap).
 */
export interface ActionButtonProps {
  to: string;
  icon: LucideIcon;
  label: string;
  iconOnly?: boolean;
  disabled?: boolean;
  className?: string;
}

export function ActionButton({
  to,
  icon: Icon,
  label,
  iconOnly = false,
  disabled = false,
  className,
}: ActionButtonProps): React.ReactElement {
  const baseClasses = cn(
    "inline-flex h-10 shrink-0 items-center gap-2 rounded-md",
    "border border-[color:var(--color-border)] bg-white px-3",
    "text-sm font-medium text-[color:var(--color-text-primary)]",
    "transition-colors duration-150 ease-out",
    iconOnly && "aspect-square px-0",
    className,
  );
  const content = (
    <>
      <Icon
        aria-hidden="true"
        strokeWidth={2}
        className="size-4 shrink-0 text-[color:var(--color-text-secondary)]"
      />
      {!iconOnly && <span className="whitespace-nowrap">{label}</span>}
    </>
  );
  if (disabled) {
    return (
      <span
        role="link"
        aria-disabled="true"
        aria-label={iconOnly ? label : undefined}
        className={cn(baseClasses, "cursor-not-allowed opacity-50")}
      >
        {content}
      </span>
    );
  }
  return (
    <Link
      to={to}
      aria-label={iconOnly ? label : undefined}
      className={cn(
        baseClasses,
        "hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-surface-sunken)]",
      )}
    >
      {content}
    </Link>
  );
}

ActionButton.displayName = "ActionButton";
